/**
 * Tile pushes that arrived for a shot the store doesn't know yet.
 *
 * When Unity adds a shot/scene from the board it force-renders the new tile and sends a
 * targeted sceneReady/thumbnailChanged in the same frame as the add — before the async
 * setSqlitePath{refresh:true} reload has introduced the row. markSceneReady /
 * markShotThumbnail can only patch shots that already exist, so those pushes used to be
 * dropped and the tile stayed gray until the whole-project rebroadcast landed seconds
 * later. They are stashed here instead, and the sqliteBridge carry-over grafts them onto
 * the incoming row the moment the reload introduces it.
 *
 * Deliberately not reactive: nothing renders from this map, it is read once per reload.
 *
 * Not every entry is short-lived: the only consumer is the carry-over's new-shot branch, so an entry
 * the reload never treats as new (e.g. a push naming a shot the store already holds under a different
 * scene id) is never taken, and the TTL below only runs on insert — it can outlive 60s until the next
 * stash prunes it or a non-preserveUiState load clears the map. Bounded (one entry per such shot) and
 * content-safe (it holds that shot's own Unity-authored URL), so leaving it to rot is by design.
 */
import { mergeTileVideo } from './tileVideoMerge.ts';

export interface PendingTileUpdate {
  thumbnailDataUrl?: string;
  videoObjectUrl?: string;
  thumbHash?: string;
  videoHash?: string;
  /** 20261002 - a later push's `videoCleared` (tileVideoMerge.ts): the stash drops the URL an earlier
   *  push brought, so the graft never resurrects a video Unity has since deleted. */
  videoCleared?: boolean;
  /** Insert time — see PENDING_TTL_MS. */
  stashedAt: number;
}

/** Entries older than this are assumed orphaned (the reload that would consume them never came). */
const PENDING_TTL_MS = 60_000;

const pending = new Map<number, PendingTileUpdate>();

/**
 * Stash a push for a not-yet-known shot. Repeated pushes for the same id merge field by
 * field (mirroring markSceneReady's `fresh.x ?? shot.x`) so a thumbnailChanged carrying
 * only a thumbnail can't erase a videoObjectUrl an earlier sceneReady brought — while a later
 * `videoCleared` does take it away (tileVideoMerge.ts, the shared rule).
 */
export function stashPendingTileUpdate(shotId: number, update: Omit<PendingTileUpdate, 'stashedAt'>): void {
  const now = Date.now();
  // Opportunistic prune (no timer): keeps the map bounded if reloads stop arriving.
  for (const [id, entry] of pending) {
    if (now - entry.stashedAt > PENDING_TTL_MS) pending.delete(id);
  }
  const prev = pending.get(shotId);
  pending.set(shotId, {
    thumbnailDataUrl: update.thumbnailDataUrl ?? prev?.thumbnailDataUrl,
    thumbHash: update.thumbHash ?? prev?.thumbHash,
    ...mergeTileVideo(prev ?? {}, update),
    stashedAt: now,
  });
}

/** Read AND remove — a stashed push is grafted exactly once. */
export function takePendingTileUpdate(shotId: number): PendingTileUpdate | undefined {
  const entry = pending.get(shotId);
  if (entry) pending.delete(shotId);
  return entry;
}

/** Called on every non-preserveUiState load: shot ids collide across projects, so nothing may survive a project switch. */
export function clearPendingTileUpdates(): void {
  pending.clear();
}
